import React, { useState } from 'react';
import axios from 'axios';
import xlentLogo from '../assets/logo192.png';

const TrackOrder = () => {
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  // API URL with multiple fallbacks for safety
  const getApiUrl = () => {
    // 1. Try environment variable first
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }
    
    // 2. Try to detect production vs development
    if (process.env.NODE_ENV === 'production') {
      // 3. Production fallbacks - API is always on main domain
      const possibleUrls = [
        `https://teenagefashion.in/backend/api`,           // Main domain
        `https://www.teenagefashion.in/backend/api`        // WWW version
      ];
      
      // Return the first one that makes sense
      return possibleUrls[0];
    }
    
    // 4. Development fallback
    return 'http://localhost/newfashion/backend/api';
  };

  const API_URL = getApiUrl();
  
  // Debug logging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log('🔧 API URL being used:', API_URL);
  }

  const handleTrackOrders = async (e) => {
    e.preventDefault();
    if (!phone.trim() || !pin.trim()) {
      setError('Please enter both Phone number and PIN');
      return;
    }
    setLoading(true);
    setError('');
    setCustomer(null);
    setOrders([]);
    try {
      const response = await axios.get(`${API_URL}/track_order.php`, {
        params: {
          phone: phone,
          pin: pin
        }
      });
      if (response.data.success) {
        setCustomer(response.data.customer);
        setOrders(response.data.orders);
      } else {
        setError(response.data.error || 'Customer not found. Please check your Phone number and PIN.');
      }
    } catch (err) {
      console.error('Track orders error:', err);
      console.error('API URL attempted:', API_URL);
      
      if (err.response) {
        // Server responded with error status
        setError(`Server error (${err.response.status}). Please try again.`);
      } else if (err.request) {
        // Network error - couldn't reach server
        setError('Cannot connect to server. Please check your internet connection.');
      } else {
        // Other error
        setError('Failed to track orders. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Normalize status from DB to internal key
  const normalizeStatus = (status) => {
    if (!status) return '';
    const s = status.toLowerCase().replace(/\s+/g, '_');
    if (s === 'in_progress' || s === 'inprogress') return 'in_progress';
    if (s === 'pending') return 'pending';
    if (s === 'completed') return 'completed';
    if (s === 'delivered') return 'delivered';
    return s;
  };

  const getOrdersByStatus = (statusKey) => {
    return orders.filter(order => normalizeStatus(order.status) === statusKey);
  };

  const getStatusColor = (status) => {
    switch (normalizeStatus(status)) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (normalizeStatus(status)) {
      case 'pending':
        return 'PENDING';
      case 'in_progress':
        return 'IN PROGRESS';
      case 'completed':
        return 'COMPLETED';
      case 'delivered':
        return 'DELIVERED';
      default:
        return status?.toUpperCase() || 'UNKNOWN';
    }
  };

  // Helper function to get image URL
  const getImageUrl = (image_path) => {
    if (!image_path) return null;
    if (image_path.startsWith('http')) return image_path;
    return `${process.env.NODE_ENV === 'production' ? 'https://teenagefashion.in' : 'http://localhost/newfashion'}/${image_path}`;
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col md:flex-row">
        {/* Left: Form Card */}
        <div className="w-full md:w-1/2 flex flex-col justify-center items-center px-2 py-6 sm:px-4 sm:py-12 bg-transparent md:border-r md:border-gray-700 min-h-[320px]">
          <div className="max-w-md w-full">
            <div className="flex justify-center mb-4">
              <img src={xlentLogo} alt="Teenage Fashion Logo" className="h-16 w-auto rounded-lg shadow-lg" />
            </div>
            <div className="bg-gray-800/80 backdrop-blur-md border border-red-800 border-[1px] rounded-2xl p-4 sm:p-6 mb-2 shadow-xl">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-1 text-center">Track Your Order</h2>
              <p className="text-gray-300 text-center mb-6 text-sm sm:text-base">Enter your Phone number and PIN to view your order status and details.</p>
              <form onSubmit={handleTrackOrders} className="space-y-5">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-200 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Enter Phone Number"
                    className="w-full px-4 py-3 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 text-base bg-gray-700 text-white placeholder-gray-400"
                    required
                    autoComplete="tel"
                  />
                </div>
                <div>
                  <label htmlFor="pin" className="block text-sm font-medium text-gray-200 mb-1">PIN</label>
                  <input
                    type="text"
                    id="pin"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="Enter PIN"
                    className="w-full px-4 py-3 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 text-base bg-gray-700 text-white placeholder-gray-400"
                    required
                    autoComplete="off"
                  />
                </div>
                {error && (
                  <div className="p-2 bg-red-900/50 border border-red-700 text-red-200 rounded text-sm text-center">{error}</div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-red-800 text-white py-3 rounded-full font-semibold hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-base mt-2 transition-all duration-200"
                  style={{ minHeight: 48 }}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Searching...
                    </div>
                  ) : (
                    'Track Order'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Right: Order Details */}
        <div className="w-full md:w-1/2 flex flex-col justify-center items-center px-2 py-6 sm:px-4 sm:py-12 bg-transparent min-h-[320px]">
          <div className="max-w-2xl w-full">
            {!customer ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 text-base opacity-70 min-h-[200px]">
                <span className="mb-2">Order details will appear here after you search.</span>
                <svg width="60" height="60" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="opacity-20"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a4 4 0 004 4h10a4 4 0 004-4V7M16 3v4M8 3v4m-5 4h18" /></svg>
              </div>
            ) : (
              <div className="space-y-4 animate-fadeIn">
                {/* Greeting */}
                <div className="text-center mb-6">
                  <div className="text-2xl sm:text-3xl font-extrabold text-white mb-2 tracking-tight leading-tight" style={{letterSpacing: '-0.01em'}}>
                    Hello, <span className="text-pink-300">{customer.name}!</span>
                  </div>
                  <div className="text-base sm:text-lg text-gray-300 font-medium mb-1">
                    Thanks for shopping at Teenage Fashion.
                  </div>
                  <div className="text-sm sm:text-base text-gray-400">
                  Hang tight — your order is being tailored with care.
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">
                    Scroll down to see full details
                  </div>
                </div>
                {/* Orders Status Card - Floating, Material, Glassmorphism */}
                <div className="relative z-10 mx-auto bg-gray-800/60 backdrop-blur-xl border-2 border-gray-700/60 shadow-2xl rounded-3xl p-4 sm:p-8 mb-4 mt-2 ring-1 ring-red-200/20">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                    <div className="text-center p-2 bg-yellow-900/30 rounded-lg border border-yellow-700/30">
                      <div className="text-lg font-bold text-yellow-300">{getOrdersByStatus('pending').length}</div>
                      <div className="text-xs text-yellow-200">Pending</div>
                    </div>
                    <div className="text-center p-2 bg-blue-900/30 rounded-lg border border-blue-700/30">
                      <div className="text-lg font-bold text-blue-300">{getOrdersByStatus('in_progress').length}</div>
                      <div className="text-xs text-blue-200">In Progress</div>
                    </div>
                    <div className="text-center p-2 bg-green-900/30 rounded-lg border border-green-700/30">
                      <div className="text-lg font-bold text-green-300">{getOrdersByStatus('completed').length}</div>
                      <div className="text-xs text-green-200">Completed</div>
                      {getOrdersByStatus('completed').length > 0 && (
                        <div className="text-xs text-green-200 mt-1">Please collect your order from the shop.</div>
                      )}
                    </div>
                  </div>
                  {/* Orders Table */}
                  {orders.length === 0 ? (
                    <div className="text-center py-6 text-gray-400">No orders found for this customer.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-700 text-xs">
                        <thead className="bg-gray-700/50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-300 uppercase tracking-wider">Status</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-300 uppercase tracking-wider">Order Image</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-300 uppercase tracking-wider">Total Amount</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-300 uppercase tracking-wider">Balance</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-300 uppercase tracking-wider">Quantity</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-300 uppercase tracking-wider">Delivery Date</th>
                          </tr>
                        </thead>
                        <tbody className="bg-gray-800/30 divide-y divide-gray-700">
                          {orders.map((order) => (
                            <tr key={order.id} className="hover:bg-gray-700/30">
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                                  {getStatusText(order.status)}
                                </span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {order.image_path ? (
                                  <img
                                    src={getImageUrl(order.image_path)}
                                    alt="Order"
                                    className="w-12 h-12 object-cover rounded border border-gray-600 cursor-pointer transition-transform duration-150 hover:scale-105"
                                    loading="lazy"
                                    onClick={() => {
                                      setSelectedImage(getImageUrl(order.image_path));
                                      setShowImageModal(true);
                                    }}
                                  />
                                ) : (
                                  <div className="w-12 h-12 flex items-center justify-center bg-gray-700 rounded border border-gray-600 text-gray-400 text-lg">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap font-medium text-white">₹{order.total_amount}</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className={order.balance > 0 ? 'text-red-400 font-medium' : 'text-green-400 font-medium'}>
                                  ₹{order.balance}
                                </span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-white">{order.quantity}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-white">{formatDate(order.delivery_date)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {/* Scroll indicator for mobile */}
                      <div className="block sm:hidden text-center text-xs text-gray-400 mt-2 select-none">
                        <svg className="inline-block mr-1 align-middle" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                        Scroll to see more
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Modern Help Section - always at the bottom, centered */}
      <div className="w-full flex justify-center items-center mt-6 mb-6 px-2">
        <div className="max-w-xl w-full bg-gray-800/70 backdrop-blur-md border border-red-800 border-[1px] rounded-2xl p-4 flex flex-col items-center shadow-xl">
          <div className="text-center text-base sm:text-lg font-medium text-white mb-2">
            Need any help with your PIN or order enquiry?<br />
            <span className="text-gray-300 font-normal">We are just a message away.</span>
          </div>
          <div className="flex flex-row gap-6 items-center justify-center mt-2">
            {/* WhatsApp */}
            <a href="https://wa.me/919633343342" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform" title="Chat on WhatsApp">
              {/* Modern WhatsApp SVG */}
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="18" cy="18" r="18" fill="#25D366"/>
                <path d="M27.5 18c0-5.24-4.26-9.5-9.5-9.5s-9.5 4.26-9.5 9.5c0 1.67.44 3.23 1.21 4.58L8 28l5.6-1.47A9.46 9.46 0 0 0 18 27.5c5.24 0 9.5-4.26 9.5-9.5zm-9.5 8.13c-1.36 0-2.68-.36-3.81-1.04l-.27-.16-3.32.87.89-3.23-.18-.28A7.47 7.47 0 1 1 18 25.47zm4.13-5.6c-.23-.12-1.36-.67-1.57-.75-.21-.08-.36-.12-.5.12-.15.23-.57.75-.7.9-.13.15-.26.17-.49.06-.23-.12-.97-.36-1.85-1.13-.68-.6-1.14-1.34-1.28-1.57-.13-.23-.01-.35.1-.46.1-.1.23-.26.34-.39.11-.13.15-.23.23-.38.08-.15.04-.28 0-.39-.04-.11-.5-1.2-.68-1.65-.18-.43-.36-.37-.5-.38-.13-.01-.28-.01-.43-.01-.15 0-.39.06-.6.28-.21.22-.8.78-.8 1.9 0 1.12.82 2.2.94 2.35.12.15 1.61 2.47 4.13 3.36.58.2 1.03.32 1.38.41.58.15 1.11.13 1.53.08.47-.07 1.36-.56 1.56-1.1.19-.54.19-1 .13-1.1-.06-.1-.21-.16-.44-.28z" fill="#fff"/>
              </svg>
            </a>
            {/* Call */}
            <a href="tel:+919633343342" className="hover:scale-110 transition-transform" title="Call us">
              {/* Modern Phone SVG */}
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="18" cy="18" r="18" fill="#25A7DF"/>
                <path d="M25.6 22.7l-2.1-1c-.5-.2-1.1-.1-1.5.3l-.7.7c-2.1-1.1-3.8-2.8-4.9-4.9l.7-.7c.4-.4.5-1 .3-1.5l-1-2.1c-.3-.6-1-.9-1.6-.7l-1.8.4c-.6.1-1 .7-1 1.3 0 6.1 5 11.1 11.1 11.1.6 0 1.2-.4 1.3-1l.4-1.8c.1-.6-.1-1.3-.7-1.6z" fill="#fff"/>
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full flex justify-center items-center mt-4 mb-4 px-2">
        <div className="text-center text-sm text-gray-400">
          Made by Anzil © 2025 | Reach me: <a href="tel:+918590440546" className="text-pink-300 hover:text-pink-200 transition-colors">+91-8590440546</a>
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              className="absolute -top-12 right-0 text-white hover:text-gray-300 text-3xl font-bold z-10"
              onClick={() => setShowImageModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <img
              src={selectedImage}
              alt="Order"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default TrackOrder; 